/**
 * The app's colour theme — the Settings "Dark mode" toggle, and everything that makes the
 * choice survive a browser restart.
 *
 * The token layer has been dark-ready since #6496: every `--ds-*` semantic resolves to its dark
 * value under `[data-theme='dark']` (styles/design-tokens.css), and the brand roles remap under
 * `[data-brand="symphony"][data-theme="dark"]` (styles/globals.css). This module owns the ONE
 * attribute that turns that on, plus where the choice is written.
 *
 * ── Persistence: the same shape the language pick uses (`lib/i18n.ts`) ────────────────────────
 * Written to BOTH `localStorage` and a cookie. `localStorage` is the client-side source of truth;
 * the cookie exists so the SERVER can read the pick in `_document.getInitialProps` and stamp
 * `data-theme` into the served HTML. Without the cookie every load paints the LIGHT palette and
 * then flips to dark after hydration — a full-page white flash on every navigation, which is the
 * single most visible way to get dark mode wrong.
 *
 * ── Absent = follow the OS, exactly as the phones do ─────────────────────────────────────────
 * The native apps resolve an ABSENT preference as "follow the system appearance"
 * (`DarkModePreference.effectiveDark(override:systemDark:)` — `nil` means follow), and web now
 * matches: a browser/OS set to dark gets a dark app on the FIRST visit, and keeps following the OS
 * until the user flips the Settings toggle. Flipping it stores an EXPLICIT `'light'`/`'dark'`
 * (never "no key"), and from then on the stored pick is the whole answer — the OS is not consulted
 * again, so a user who chose light on a dark machine stays light.
 *
 * Web only learns the OS preference in the BROWSER (`prefers-color-scheme`); no request header
 * carries it. So the server stamps what the cookie says and `public/theme-init.js` — a pre-paint
 * script — corrects the no-pick case before the first frame. Nothing writes the system-derived
 * value to storage: persisting it would freeze the app against the next OS flip and quietly turn
 * "never chose" into "chose".
 *
 * ⚠️ "An explicit pick is the whole answer" is true FOR AS LONG AS THE PICK SURVIVES, and on web
 * that is weaker than it sounds — weaker than it was before the default followed the OS. Safari's
 * ITP caps a `document.cookie`-written cookie at 7 days whatever `max-age` says, and evicts
 * script-writable storage after 7 days without interaction. When the pick used to vanish the app
 * fell back to LIGHT, which is what a light-choosing user had picked anyway, so eviction was
 * invisible to them; now it falls back to the OS, so a Safari user on a dark Mac who chose light
 * and stays away eight days comes back to dark. The phones do not have this (UserDefaults
 * survives). The durable fix is to persist the pick account-side the way the language pick is —
 * deliberately NOT done here, and written down so the next person meets the trade-off rather than
 * discovering it from a support ticket.
 *
 * Within a session the pick holds unconditionally, storage or no storage: `_app` also keeps a
 * `themeChosen` ref, because `setStoredTheme` swallows a failed write (private mode, cookies
 * blocked, quota) and a guard that only asked storage would let the OS listener repaint over a
 * choice the user had just made.
 *
 * ── Nothing here is a gate ───────────────────────────────────────────────────────────────────
 * This module reads and writes a preference; it does not decide who may HAVE one, and nothing else
 * does either: the stored pick is the whole decision, exactly as it is on the phones.
 */

export type Theme = 'light' | 'dark';

/** The persisted pick. `null` = never chosen. Same key in `localStorage` and the cookie. */
export const THEME_STORAGE_KEY = 'orion-web.theme';

/** One year, matching the locale cookie — a display choice should not quietly expire. */
const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const isTheme = (value: string | null | undefined): value is Theme => value === 'light' || value === 'dark';

/** The OS appearance query. Kept next to the storage key: `public/theme-init.js` hardcodes both. */
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The theme to render, given the stored pick and what the OS asks for. An explicit pick always
 * wins; absent → the system appearance (see the header note) — the same `override ?? systemDark`
 * the phones resolve. The one place that decides, so SSR, the pre-paint script and the client can
 * never disagree about what "no preference" means.
 *
 * `systemDark` defaults to `false` for the SSR caller alone: a request carries no
 * `prefers-color-scheme`, so the server renders the cookie's answer and treats "no pick" as light,
 * and `public/theme-init.js` re-resolves it in the browser before anything is painted.
 */
export function resolveTheme(stored: Theme | null, systemDark = false): Theme {
  return stored ?? (systemDark ? 'dark' : 'light');
}

/** Does the browser/OS ask for dark right now? `false` on the server and where unsupported. */
export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(SYSTEM_DARK_QUERY).matches;
}

/**
 * Call `onChange` whenever the OS appearance flips, and return the unsubscribe. The app follows
 * these live only while nothing is stored — the caller re-reads the pick on each event, so the
 * subscription goes inert the moment the user touches the toggle (native gets this for free from
 * `@Environment(\.colorScheme)`).
 */
export function subscribeSystemTheme(onChange: (system: Theme) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(SYSTEM_DARK_QUERY);
  const handler = (e: MediaQueryListEvent) => onChange(e.matches ? 'dark' : 'light');
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}

/** Parse the pick out of a raw `Cookie:` header (SSR) or `document.cookie` (client). */
export function themeFromCookieHeader(cookieHeader?: string | null): Theme | null {
  if (!cookieHeader) return null;
  const escaped = THEME_STORAGE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`));
  if (!match?.[1]) return null;
  let raw = '';
  try {
    raw = decodeURIComponent(match[1]);
  } catch {
    raw = match[1];
  }
  return isTheme(raw) ? raw : null;
}

/** The stored pick, `localStorage` first, then the cookie (private mode blocks the former). */
export function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(raw)) return raw;
  } catch {
    // Fall through to the cookie when localStorage is unavailable (private mode).
  }
  return typeof document === 'undefined' ? null : themeFromCookieHeader(document.cookie);
}

export function setStoredTheme(theme: Theme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Private-mode / storage-disabled: rely on the cookie below to persist.
  }
  try {
    document.cookie = `${THEME_STORAGE_KEY}=${theme}; path=/; max-age=${THEME_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // Cookies disabled too: the choice just won't survive a reload.
  }
}


/**
 * Routes that always paint LIGHT, whatever the reader's pick or OS says: the public profile link
 * (`/me/<handle>`). Its colours are the OWNER's palette — a light page an owner designed — and the DS cards
 * on it would flip dark on top of that light ground. Its reader is a stranger whose dark preference was
 * never about this page. Matched on both the request path and the Next route pattern (both start `/me/`).
 * Duplicated in public/theme-init.js (a static asset cannot import this); test/dark-mode.test.ts pins both.
 */
export const LIGHT_PINNED_PATH = /^\/me\//i;

export function isLightPinnedPath(pathname: string | undefined | null): boolean {
  return typeof pathname === 'string' && LIGHT_PINNED_PATH.test(pathname);
}

/**
 * Put the theme on `<html>`. Dark STAMPS `data-theme="dark"`; light REMOVES the attribute rather
 * than writing `data-theme="light"` — components that paint a permanently-dark island stamp
 * `data-theme="dark"` on their own subtree (see `MobileSidebar`), and a `light` value on the root
 * would be a second thing the cascade has to reason about for no gain. A light-pinned route always gets
 * the light root.
 */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark' && !isLightPinnedPath(window.location?.pathname)) root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
}
